import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductCard from "@/components/base/ProductCard";
import { categories } from "@/mocks/categories";
import { featuredProducts, testimonialData } from "@/mocks/products";

function HeroSection() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");

  return (
    <section className="relative w-full h-[600px] md:h-[700px] flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20modern%20lifestyle%20scene%20with%20carefully%20arranged%20premium%20products%20on%20a%20warm%20cream%20surface%2C%20soft%20natural%20lighting%20from%20large%20window%2C%20elegant%20aesthetic%20product%20flat%20lay%20with%20headphones%20watch%20candle%20and%20notebook%2C%20warm%20terracotta%20and%20beige%20tones%2C%20editorial%20photography&width=1800&height=900&seq=hero-bg-store-01&orientation=landscape"
          alt="Galore hero"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/45"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto w-full">
          <div className={`flex flex-col ${isAr ? "lg:flex-row-reverse" : "lg:flex-row"} items-center gap-10 lg:gap-16`}>
            {/* Left content */}
            <div className={`flex-1 text-center ${isAr ? "lg:text-right" : "lg:text-left"}`}>
              {/* Social proof */}
              <div className={`flex items-center gap-3 mb-6 ${isAr ? "lg:justify-end" : "lg:justify-start"} justify-center`}>
                <div className="flex -space-x-2">
                  <div className="w-9 h-9 rounded-full border-2 border-white overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Friendly%20professional%20portrait%20headshot%20on%20clean%20cream%20background%2C%20soft%20natural%20lighting&width=80&height=80&seq=avatar-s1-hero&orientation=squarish"
                      alt="Customer"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-9 h-9 rounded-full border-2 border-white overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Warm%20friendly%20portrait%20headshot%20of%20a%20woman%20on%20clean%20cream%20background%2C%20soft%20natural%20lighting%2C%20professional%20style&width=80&height=80&seq=avatar-s2-hero&orientation=squarish"
                      alt="Customer"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-9 h-9 rounded-full border-2 border-white overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Confident%20professional%20portrait%20headshot%20of%20a%20man%20on%20clean%20cream%20background%2C%20soft%20natural%20lighting%2C%20approachable%20expression&width=80&height=80&seq=avatar-s3-hero&orientation=squarish"
                      alt="Customer"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <span className="text-sm text-white/90 font-medium">
                  {t("hero.trusted_by")}
                </span>
              </div>

              {/* Main title */}
              <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading text-white leading-tight mb-6">
                {t("hero.title_line1")}
                <br />
                <span className="text-accent-400">{t("hero.title_line2")}</span>
              </h2>

              {/* Subtitle */}
              <p className={`text-base md:text-lg text-white/80 max-w-xl mb-8 ${isAr ? "lg:ml-auto" : "lg:mr-auto"} mx-auto lg:mx-0 leading-relaxed`}>
                {t("hero.subtitle")}
              </p>

              {/* Buttons */}
              <div className={`flex flex-col sm:flex-row gap-3 ${isAr ? "lg:justify-end" : "lg:justify-start"} justify-center`}>
                <Link
                  to="/products"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  {t("hero.shop_now")}
                  <i className={`${isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}`}></i>
                </Link>
                <Link
                  to="/products"
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-full hover:bg-white/20 border border-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  {t("hero.explore")}
                </Link>
              </div>
            </div>

            {/* Right content - empty for balance, the background image does the heavy lifting */}
            <div className="hidden lg:block flex-1"></div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-white/60 text-2xl"></i>
      </div>
    </section>
  );
}

function CategoriesSection() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 border border-secondary-300 rounded-full text-xs font-medium text-secondary-700 uppercase tracking-wider mb-4">
              {t("categories.label")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-4">
              {t("categories.title")}
            </h2>
            <p className="text-base text-foreground-600 max-w-xl mx-auto">
              {t("categories.subtitle")}
            </p>
          </div>

          {/* Categories grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {categories.map((cat) => {
              const name = isAr ? cat.nameAr : cat.nameEn;
              return (
                <Link
                  key={cat.id}
                  to={`/products/${cat.slug}`}
                  className="group flex flex-col items-center p-4 md:p-5 rounded-xl bg-background-50 border border-background-200 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl overflow-hidden bg-background-100 mb-3 group-hover:scale-105 transition-transform duration-300">
                    <img
                      src={cat.imageUrl}
                      alt={name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 text-center whitespace-nowrap">
                    {name}
                  </h3>
                  <span className="text-xs text-foreground-500 mt-1">
                    {cat.itemCount} items
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturedProductsSection() {
  const { t } = useTranslation();

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block px-4 py-1.5 border border-secondary-300 rounded-full text-xs font-medium text-secondary-700 uppercase tracking-wider mb-4">
              {t("featured.label")}
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 mb-4">
              {t("featured.title")}
            </h2>
            <p className="text-base text-foreground-600 max-w-xl mx-auto">
              {t("featured.subtitle")}
            </p>
          </div>

          {/* Products grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} {...product} imageUrl={product.images[0]} />
            ))}
          </div>

          {/* View all button */}
          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 px-8 py-3.5 border-2 border-foreground-900 text-foreground-900 font-semibold rounded-full hover:bg-foreground-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              {t("featured.view_all")}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialSection() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const [current, setCurrent] = useState(0);
  const testimonial = testimonialData[current];
  const text = isAr ? testimonial.textAr : testimonial.text;

  const goNext = () => setCurrent((prev) => (prev + 1) % testimonialData.length);
  const goPrev = () => setCurrent((prev) => (prev - 1 + testimonialData.length) % testimonialData.length);

  return (
    <section className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Title */}
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
            {t("testimonials.title")}
          </h2>

          {/* Rating badge */}
          <div className="mb-8">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-foreground-950 text-white rounded-full text-sm font-medium">
              <i className="ri-star-fill text-accent-400"></i>
              {t("testimonials.rating")}
            </span>
          </div>

          {/* Quote */}
          <blockquote className="mb-10">
            <p className="text-lg md:text-xl lg:text-2xl font-heading italic text-foreground-800 leading-relaxed">
              &ldquo;{text}&rdquo;
            </p>
          </blockquote>

          {/* Author */}
          <div className={`flex items-center gap-4 ${isAr ? "flex-row-reverse text-right" : ""}`}>
            <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
              <img
                src={testimonial.avatarUrl}
                alt={testimonial.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <p className="font-semibold text-foreground-900">{testimonial.name}</p>
              <p className="text-sm text-foreground-500">{testimonial.role}</p>
            </div>

            {/* Nav arrows */}
            <div className={`flex items-center gap-2 ${isAr ? "mr-auto" : "ml-auto"}`}>
              <button
                onClick={goPrev}
                className="w-10 h-10 rounded-lg border border-foreground-300 flex items-center justify-center text-foreground-600 hover:bg-foreground-100 hover:border-foreground-400 transition-colors cursor-pointer"
                aria-label="Previous"
              >
                <i className={isAr ? "ri-arrow-right-line" : "ri-arrow-left-line"}></i>
              </button>
              <button
                onClick={goNext}
                className="w-10 h-10 rounded-lg bg-foreground-950 flex items-center justify-center text-white hover:bg-foreground-800 transition-colors cursor-pointer"
                aria-label="Next"
              >
                <i className={isAr ? "ri-arrow-left-line" : "ri-arrow-right-line"}></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { t } = useTranslation();

  return (
    <section className="relative w-full h-[500px] md:h-[550px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20cozy%20modern%20living%20room%20interior%20with%20soft%20natural%20light%20streaming%20through%20windows%2C%20comfortable%20textiles%20in%20terracotta%20and%20cream%20tones%2C%20minimalist%20decor%2C%20warm%20and%20inviting%20atmosphere%2C%20editorial%20interior%20photography&width=1800&height=900&seq=cta-bg-store-02&orientation=landscape"
          alt="CTA background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/55"></div>
      </div>

      <div className="relative z-10 w-full px-4 md:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white uppercase tracking-wide mb-6">
          {t("cta.title")}
        </h2>
        <p className="text-base md:text-lg text-white/80 mb-4 max-w-lg mx-auto">
          {t("cta.subtitle_line1")}
        </p>
        <p className="text-base md:text-lg text-white/80 mb-10 max-w-lg mx-auto">
          {t("cta.subtitle_line2")}
        </p>
        <Link
          to="/products"
          className="inline-flex items-center gap-3 px-10 py-4 bg-foreground-950 text-white font-semibold rounded-full hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap text-sm group"
        >
          <span className="w-8 h-8 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-shopping-bag-line text-white text-sm"></i>
          </span>
          <span>{t("cta.button")}</span>
          <i className="ri-arrow-right-up-line group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"></i>
        </Link>
      </div>
    </section>
  );
}

function NewsletterSection() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      return;
    }
    if (!email.trim()) return;
    setStatus("loading");
    try {
      const formData = new URLSearchParams();
      formData.append("email", email);
      await fetch("https://readdy.ai/api/form/d8tuj34al24muhn2rbqg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });
      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
            {t("newsletter.title")}
          </h2>
          <p className="text-base text-foreground-600 mb-8">
            {t("newsletter.subtitle")}
          </p>

          {status === "success" ? (
            <div className="bg-accent-100 text-accent-800 rounded-xl p-6">
              <i className="ri-checkbox-circle-line text-3xl mb-2 block"></i>
              <p className="font-medium">{t("newsletter.success")}</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input
                name="website_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("newsletter.placeholder")}
                required
                className="flex-1 px-5 py-3.5 text-sm bg-background-100 border border-background-200 rounded-full text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
              <button
                type="submit"
                disabled={status === "loading"}
                className="px-8 py-3.5 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
              >
                {status === "loading" ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : (
                  t("newsletter.button")
                )}
              </button>
            </form>
          )}

          <p className="text-xs text-foreground-400 mt-4">
            <i className="ri-lock-line mr-1"></i>
            {t("newsletter.privacy")}
          </p>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");

  return (
    <div className="min-h-screen" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main>
        <HeroSection />
        <CategoriesSection />
        <FeaturedProductsSection />
        <TestimonialSection />
        <CTASection />
        <NewsletterSection />
      </main>
      <Footer />
    </div>
  );
}